import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { updateQuestionAnswer } from '../utils/questionCompletenessTracker';

interface InlineQuestionEditorProps {
  configItemId: string;
  companyId: string;
  questionId: string;
  questionText: string;
  currentAnswer: string;
  accessToken: string;
  onAnswerUpdated?: (newAnswer: string) => void;
  mode?: 'inline' | 'dialog';
}

export function InlineQuestionEditor({
  configItemId,
  companyId,
  questionId,
  questionText,
  currentAnswer,
  accessToken,
  onAnswerUpdated,
  mode = 'inline',
}: InlineQuestionEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedAnswer, setEditedAnswer] = useState(currentAnswer);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!editedAnswer.trim()) {
      toast.error('Answer cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      await updateQuestionAnswer(
        configItemId,
        companyId,
        questionId,
        editedAnswer,
        accessToken
      );

      toast.success('Answer updated');
      setIsEditing(false);

      if (onAnswerUpdated) {
        onAnswerUpdated(editedAnswer);
      }
    } catch (error) {
      console.error('Error saving answer:', error);
      toast.error('Failed to save answer');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedAnswer(currentAnswer);
    setIsEditing(false);
  };

  if (mode === 'dialog') {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsEditing(true)}
          className="h-8"
        >
          <Pencil className="w-4 h-4 mr-2" />
          Edit Answer
        </Button>

        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Answer</DialogTitle>
              <DialogDescription>
                Update the answer to this discovery question
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Question
                </label>
                <p className="text-sm text-gray-600">{questionText}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Answer
                </label>
                <Textarea
                  value={editedAnswer}
                  onChange={(e) => setEditedAnswer(e.target.value)}
                  rows={4}
                  placeholder="Enter your answer..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Inline mode
  if (!isEditing) {
    return (
      <div className="group relative">
        <p className="text-sm text-gray-700">{currentAnswer}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsEditing(true)}
          className="absolute -right-2 -top-2 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0"
        >
          <Pencil className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Textarea
        value={editedAnswer}
        onChange={(e) => setEditedAnswer(e.target.value)}
        rows={3}
        className="text-sm"
        autoFocus
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
          className="h-8"
        >
          <Check className="w-4 h-4 mr-1" />
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          disabled={isSaving}
          className="h-8"
        >
          <X className="w-4 h-4 mr-1" />
          Cancel
        </Button>
      </div>
    </div>
  );
}